import { useEffect } from "react";
import {
  hydrateTheme,
  useAppDispatch,
  useStore,
} from "lib/store/store";
import {
  applyThemeToDocument,
  DEFAULT_THEME,
  isDefaultTheme,
  readStoredTheme,
  THEME_COLORS,
} from "lib/theme";
import { cn } from "lib/utils";

function ThemeSettings() {
  const dispatch = useAppDispatch();
  const {
    themeMode,
    themeDirection,
    themeColor,
    settingsOpen,
    setThemeMode,
    setThemeDirection,
    setThemeColor,
    resetTheme,
    setSettingsOpen,
    toggleSettings,
  } = useStore();

  const isDefault = isDefaultTheme({
    mode: themeMode,
    direction: themeDirection,
    color: themeColor,
  });

  useEffect(() => {
    const prefs = readStoredTheme();
    dispatch(hydrateTheme(prefs));
    applyThemeToDocument(prefs);
  }, [dispatch]);

  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [settingsOpen, setSettingsOpen]);

  return (
    <>
      {/* Floating settings button — bottom right */}
      <button
        type="button"
        aria-label="Open theme settings"
        aria-expanded={settingsOpen}
        onClick={toggleSettings}
        className={cn(
          "fixed bottom-6 right-6 z-[1000] flex size-12 items-center justify-center rounded-full text-white shadow-lg transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:ring-offset-2",
          settingsOpen && "scale-95 opacity-0 pointer-events-none"
        )}
        style={{ backgroundColor: "var(--theme-accent)" }}
      >
        <i className="fas fa-cog text-lg" aria-hidden />
      </button>

      {/* Backdrop */}
      <div
        aria-hidden={!settingsOpen}
        onClick={() => setSettingsOpen(false)}
        className={cn(
          "fixed inset-0 z-[1001] bg-black/40 transition-opacity duration-300",
          settingsOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />

      {/* Settings panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Theme settings"
        className={cn(
          "fixed inset-y-0 right-0 z-[1002] flex w-[min(100vw,320px)] flex-col bg-white text-[#1e293b] shadow-2xl transition-transform duration-300 ease-out",
          settingsOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-bold text-[#0f172a]">Settings</h2>
          <button
            type="button"
            aria-label="Close settings"
            onClick={() => setSettingsOpen(false)}
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <i className="fas fa-times text-sm" aria-hidden />
          </button>
        </div>

        <div className="dashboard-scroll flex-1 space-y-7 overflow-y-auto px-5 py-6">
          {/* Theme Option */}
          <section>
            <h3 className="mb-3 text-sm font-semibold text-[#0f172a]">
              Theme Option
            </h3>
            <div className="grid grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={resetTheme}
                className={cn(
                  "flex flex-col items-center justify-center gap-1.5 rounded-xl bg-white px-2 py-3 text-xs font-medium text-[#334155] shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)] sm:text-sm",
                  isDefault && "ring-2 ring-[var(--theme-accent)] ring-offset-1"
                )}
              >
                <i className="fas fa-rotate-left text-[#64748b]" aria-hidden />
                Default
              </button>
              <button
                type="button"
                onClick={() => setThemeMode("light")}
                className={cn(
                  "flex flex-col items-center justify-center gap-1.5 rounded-xl bg-white px-2 py-3 text-xs font-medium text-[#334155] shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)] sm:text-sm",
                  themeMode === "light" &&
                    !isDefault &&
                    "ring-2 ring-[var(--theme-accent)] ring-offset-1"
                )}
              >
                <i
                  className="fas fa-sun"
                  style={{ color: "var(--theme-accent)" }}
                  aria-hidden
                />
                Light
              </button>
              <button
                type="button"
                onClick={() => setThemeMode("dark")}
                className={cn(
                  "flex flex-col items-center justify-center gap-1.5 rounded-xl bg-white px-2 py-3 text-xs font-medium text-[#334155] shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)] sm:text-sm",
                  themeMode === "dark" &&
                    !isDefault &&
                    "ring-2 ring-[var(--theme-accent)] ring-offset-1"
                )}
              >
                <i className="fas fa-moon text-[#64748b]" aria-hidden />
                Dark
              </button>
            </div>
          </section>

          {/* Theme Direction */}
          <section>
            <h3 className="mb-3 text-sm font-semibold text-[#0f172a]">
              Theme Direction
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setThemeDirection("ltr")}
                className={cn(
                  "flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-3.5 text-sm font-medium text-[#334155] shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)]",
                  themeDirection === "ltr" &&
                    "ring-2 ring-[var(--theme-accent)] ring-offset-1"
                )}
              >
                <span
                  className="flex flex-col gap-0.5"
                  aria-hidden
                >
                  <span className="flex items-center gap-0.5">
                    <span
                      className="h-3 w-0.5 rounded-sm"
                      style={{ backgroundColor: "var(--theme-accent)" }}
                    />
                    <span className="flex flex-col gap-0.5">
                      <span className="h-0.5 w-3 rounded-sm bg-[#94a3b8]" />
                      <span className="h-0.5 w-2.5 rounded-sm bg-[#94a3b8]" />
                      <span className="h-0.5 w-3 rounded-sm bg-[#94a3b8]" />
                    </span>
                  </span>
                </span>
                LTR
              </button>
              <button
                type="button"
                onClick={() => setThemeDirection("rtl")}
                className={cn(
                  "flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-3.5 text-sm font-medium text-[#334155] shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)]",
                  themeDirection === "rtl" &&
                    "ring-2 ring-[var(--theme-accent)] ring-offset-1"
                )}
              >
                <span className="flex items-center gap-0.5" aria-hidden>
                  <span className="flex flex-col gap-0.5">
                    <span className="h-0.5 w-3 rounded-sm bg-[#94a3b8]" />
                    <span className="h-0.5 w-2.5 self-end rounded-sm bg-[#94a3b8]" />
                    <span className="h-0.5 w-3 rounded-sm bg-[#94a3b8]" />
                  </span>
                  <span className="h-3 w-0.5 rounded-sm bg-[#64748b]" />
                </span>
                RTL
              </button>
            </div>
          </section>

          {/* Theme Colors */}
          <section>
            <h3 className="mb-3 text-sm font-semibold text-[#0f172a]">
              Theme Colors
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {/* Default brand green */}
              <button
                type="button"
                aria-label="Select default theme color"
                aria-pressed={
                  themeColor.toLowerCase() === DEFAULT_THEME.color.toLowerCase()
                }
                onClick={() => setThemeColor(DEFAULT_THEME.color)}
                className="flex items-center justify-center rounded-xl bg-white px-3 py-4 shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)]"
              >
                <span
                  className="flex size-8 items-center justify-center rounded-full text-white"
                  style={{ backgroundColor: DEFAULT_THEME.color }}
                >
                  {themeColor.toLowerCase() ===
                    DEFAULT_THEME.color.toLowerCase() && (
                    <i className="fas fa-check text-xs" aria-hidden />
                  )}
                </span>
              </button>
              {THEME_COLORS.map((swatch) => {
                const selected =
                  themeColor.toLowerCase() === swatch.value.toLowerCase();
                return (
                  <button
                    key={swatch.id}
                    type="button"
                    aria-label={`Select ${swatch.id} theme color`}
                    aria-pressed={selected}
                    onClick={() => setThemeColor(swatch.value)}
                    className="flex items-center justify-center rounded-xl bg-white px-3 py-4 shadow-[0_2px_10px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)]"
                  >
                    <span
                      className="flex size-8 items-center justify-center rounded-full text-white"
                      style={{ backgroundColor: swatch.value }}
                    >
                      {selected && (
                        <i className="fas fa-check text-xs" aria-hidden />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}

export default ThemeSettings;
